import { Route, Routes } from 'react-router-dom';
import {
  HomePage,
  OrdersPage,
  DetailsPage,
  SupportCasesPage,
  MyTeamPage,
  InventoryPage,
  StyleGuide,
  LoginPageDevise,
  AdminPage
} from './Pages';
import { useEffect, useState } from 'react';
import { MaintenancePage } from './Pages/Maintenance';
import { AuthenticatePage } from './Pages/AuthenticatePage/AuthenticatePage';
import { ResetPasswordPage } from './Pages/ResetPassword/ResetPasswordPage';
import { FeatureRoute, HomeRoute, RecordRoute } from './Compounds/FeatureRoute/FeatureRoute';
import { RestrictedRoute } from './Compounds/RestrictedRoute/RestrictedRoute';
import { Permission } from '../store/GeneralStore';
import { BulkUserCreationPage } from './Pages/UserCreationPage/BulkUserCreationPage';
import { UserCreationSummaryPage } from './Pages/UserCreationPage/UserCreationSummaryPage';
import { BulkTeamCreationPage } from './Pages/TeamCreation/BulkTeamCreationPage';
import { TeamCreationSummary } from './Pages/TeamCreation/TeamCreationSummary';
import { AddUserToTeamPage } from './Pages/AddUserToTeamPage/AddUserToTeamPage';
import { AddTeamMemberSummary } from './Pages/AddTeamMemberSummary/AddTeamMemberSummary';
import { UserSettingsPage } from './Pages/UserSettings/UserSettingsPage';
import { AddInventoryPage, AddInventorySummary } from './Pages/AddInventory';
import { isReportsPageEnabled } from '../store/User';
import { defaultFeatureFlags, FeatureFlags, getFeatureFlagSubject } from '../Api/useFeatureFlags';
import { ProjectsDetailPage, ProjectsPage } from './Pages/Projects';
import { WorkflowOrderDetailPage } from './Pages/Orders/WorkflowOrderDetailPage';
import { PhoneNumbersPage } from './Pages/PhoneNumbers/PhoneNumbersPage';
import { LineItemsPage } from './Pages/LineItems/LineItemsPage';
import { CostIntelligencePage } from './Pages/CostIntelligence/CostIntelligencePage';
import { ThoughtspotPocPage } from './Pages/ThoughtspotPocPage/ThoughtspotPocPage';
import { HexAnalyticsPage } from './Pages/HexAnalyticsPage/HexAnalyticsPage';
import {
  AdviseDashboardPage,
  AdviseSuppliersPage,
  AdviseCustomersPage,
  AdviseEngagementsPage,
  AdviseAnalysisPage,
  AdviseReportsPage,
  AdviseReportEditorPage,
  AdviseTasksPage,
  AdviseAgentToolsPage,
  AdviseUserManagementPage
} from './Pages/Advise';
import { ReportsPage } from './Pages/Reports';
import { ProtectedRoute } from './Compounds/ProtectedRoute/ProtectedRoute';

export default function AppRoutes() {
  const [featureFlags, setFeatureFlags] = useState<FeatureFlags>(defaultFeatureFlags);

  useEffect(() => {
    const featureFlagSub = getFeatureFlagSubject().subscribe((flags) => setFeatureFlags(flags));
    return () => {
      if (featureFlagSub) featureFlagSub.unsubscribe();
    };
  }, []);
  return (
    <Routes>
      <Route
        path="/login"
        element={<LoginPageDevise />}
      />
      <Route
        path={'/portal/offline'}
        element={<MaintenancePage />}
      />
      <Route
        path={'/reset'}
        element={<ResetPasswordPage />}
      />
      <Route
        path={'/authenticate-user'}
        element={<AuthenticatePage />}
      />
      <Route element={<ProtectedRoute />}>
        <Route element={<FeatureRoute feature="inventory" />}>
          <Route
            path={'/orders'}
            element={<OrdersPage />}
          />
          <Route
            path={'/orders/:id'}
            element={<WorkflowOrderDetailPage />}
          />
          <Route
            path={'/inventory'}
            element={<InventoryPage />}
          />
          {isReportsPageEnabled(featureFlags) && (
            <Route
              path={'/reports'}
              element={<ReportsPage />}
            />
          )}
        </Route>
        <Route
          path="/support-cases"
          element={
            <RestrictedRoute requiredPermissions={[[Permission.VIEW_CASES]]}>
              <SupportCasesPage />
            </RestrictedRoute>
          }
        />
        <Route
          path={'/add-inventory'}
          element={
            <RestrictedRoute requiredPermissions={[[Permission.ADD_INVENTORY_FLOW]]}>
              <AddInventoryPage />
            </RestrictedRoute>
          }
        />
        <Route
          path={'/add-inventory/summary'}
          element={
            <RestrictedRoute requiredPermissions={[[Permission.VIEW_ADVISOR, Permission.ADD_INVENTORY_FLOW]]}>
              <AddInventorySummary />
            </RestrictedRoute>
          }
        />
        {featureFlags.projects && (
          <Route
            path={'/projects'}
            element={
              <RestrictedRoute requiredPermissions={[[Permission.VIEW_PROJECTS]]}>
                <ProjectsPage />
              </RestrictedRoute>
            }
          />
        )}
        {featureFlags.projects && (
          <Route
            path={'/projects/:id'}
            element={
              <RestrictedRoute requiredPermissions={[[Permission.VIEW_PROJECTS]]}>
                <ProjectsDetailPage />
              </RestrictedRoute>
            }
          />
        )}
        <Route element={<RecordRoute />}>
          <Route
            path={'/:type/:id'}
            element={<DetailsPage />}
          />
          <Route
            path={'/account/:accountId/:type/:id'}
            element={<DetailsPage />}
          />
        </Route>
        <Route
          path={'/my-team'}
          element={<MyTeamPage />}
        />
        <Route element={<HomeRoute />}>
          <Route
            path={'/'}
            element={<HomePage />}
          />
          <Route
            path={'/*'}
            element={<HomePage />}
          />
        </Route>
        <Route
          path={'/user-settings'}
          element={<UserSettingsPage />}
        />
        <Route
          path="/account/:accountId/orders"
          element={
            <RestrictedRoute requiredPermissions={[[Permission.VIEW_ADVISOR, Permission.VIEW_EXECUTIVE]]}>
              <OrdersPage />
            </RestrictedRoute>
          }
        />
        <Route
          path="/account/:accountId/orders/:id"
          element={
            <RestrictedRoute requiredPermissions={[[Permission.VIEW_ADVISOR, Permission.VIEW_EXECUTIVE]]}>
              <WorkflowOrderDetailPage />
            </RestrictedRoute>
          }
        />
        <Route
          path="/account/:accountId/support-cases"
          element={
            <RestrictedRoute
              requiredPermissions={[[Permission.VIEW_ADVISOR, Permission.VIEW_EXECUTIVE], [Permission.VIEW_CASES]]}>
              <SupportCasesPage />
            </RestrictedRoute>
          }
        />
        <Route
          path={'/account/:accountId/add-inventory'}
          element={
            <RestrictedRoute requiredPermissions={[[Permission.VIEW_ADVISOR, Permission.ADD_INVENTORY_FLOW]]}>
              <AddInventoryPage />
            </RestrictedRoute>
          }
        />
        <Route
          path={'/account/:accountId/add-inventory/summary'}
          element={
            <RestrictedRoute requiredPermissions={[[Permission.VIEW_ADVISOR, Permission.ADD_INVENTORY_FLOW]]}>
              <AddInventorySummary />
            </RestrictedRoute>
          }
        />
        <Route
          path="/account/:accountId/inventory"
          element={
            <RestrictedRoute requiredPermissions={[[Permission.VIEW_ADVISOR, Permission.VIEW_EXECUTIVE]]}>
              <InventoryPage />
            </RestrictedRoute>
          }
        />
        <Route
          path={'/account/:accountId/my-team'}
          element={
            <RestrictedRoute requiredPermissions={[[Permission.VIEW_ADVISOR, Permission.VIEW_EXECUTIVE]]}>
              <MyTeamPage />
            </RestrictedRoute>
          }
        />
        <Route
          path={'/account/:accountId/'}
          element={
            <RestrictedRoute requiredPermissions={[[Permission.VIEW_ADVISOR, Permission.VIEW_EXECUTIVE]]}>
              <HomePage />
            </RestrictedRoute>
          }
        />
        <>
          <Route
            path={'/admin'}
            element={
              <RestrictedRoute requiredPermissions={[[Permission.VIEW_ADMIN_PAGE]]}>
                <AdminPage />
              </RestrictedRoute>
            }
          />
          <Route
            path={'/admin/new-user'}
            element={
              <RestrictedRoute requiredPermissions={[[Permission.VIEW_ADMIN_PAGE], [Permission.CREATE_USERS]]}>
                <BulkUserCreationPage />
              </RestrictedRoute>
            }
          />
          <Route
            path={'/admin/new-team'}
            element={
              <RestrictedRoute requiredPermissions={[[Permission.VIEW_ADMIN, Permission.VIEW_SUPPORT]]}>
                <BulkTeamCreationPage />
              </RestrictedRoute>
            }
          />
          <Route
            path={'/admin/new-user/summary'}
            element={
              <RestrictedRoute requiredPermissions={[[Permission.VIEW_ADMIN_PAGE], [Permission.CREATE_USERS]]}>
                <UserCreationSummaryPage />
              </RestrictedRoute>
            }
          />
          <Route
            path={'/admin/new-team/summary'}
            element={
              <RestrictedRoute requiredPermissions={[[Permission.VIEW_ADMIN_PAGE], [Permission.CREATE_USERS]]}>
                <TeamCreationSummary />
              </RestrictedRoute>
            }
          />
          <Route
            path={'/admin/teams/add-users'}
            element={
              <RestrictedRoute requiredPermissions={[[Permission.VIEW_ADMIN_PAGE], [Permission.CREATE_USERS]]}>
                <AddUserToTeamPage />
              </RestrictedRoute>
            }
          />
          <Route
            path={'/admin/teams/add-users/summary'}
            element={
              <RestrictedRoute requiredPermissions={[[Permission.VIEW_ADMIN_PAGE], [Permission.CREATE_USERS]]}>
                <AddTeamMemberSummary />
              </RestrictedRoute>
            }
          />
          <Route
            path={'/admin/teams/add-managers'}
            element={
              <RestrictedRoute requiredPermissions={[[Permission.VIEW_ADMIN_PAGE], [Permission.CREATE_USERS]]}>
                <AddUserToTeamPage />
              </RestrictedRoute>
            }
          />
          <Route
            path={'/admin/teams/add-managers/summary'}
            element={
              <RestrictedRoute requiredPermissions={[[Permission.VIEW_ADMIN_PAGE], [Permission.CREATE_USERS]]}>
                <AddTeamMemberSummary />
              </RestrictedRoute>
            }
          />
          <Route
            path={'/team-settings'}
            element={
              <RestrictedRoute requiredPermissions={[[Permission.VIEW_TEAM]]}>
                <AdminPage />
              </RestrictedRoute>
            }
          />
          <Route
            path={'/team-settings/new-user'}
            element={
              <RestrictedRoute requiredPermissions={[[Permission.VIEW_TEAM], [Permission.CREATE_TEAM_USERS]]}>
                <BulkUserCreationPage />
              </RestrictedRoute>
            }
          />
          <Route
            path={'/team-settings/new-user/summary'}
            element={
              <RestrictedRoute requiredPermissions={[[Permission.VIEW_TEAM], [Permission.CREATE_TEAM_USERS]]}>
                <UserCreationSummaryPage />
              </RestrictedRoute>
            }
          />
          <Route
            path={'/phone-numbers'}
            element={<PhoneNumbersPage />}
          />
          <Route
            path={'/line-items'}
            element={<LineItemsPage />}
          />
          {featureFlags.thoughtspot_poc && (
            <Route
              path={'/analytics'}
              element={
                <RestrictedRoute requiredPermissions={[[Permission.VIEW_THOUGHTSPOT]]}>
                  <ThoughtspotPocPage />
                </RestrictedRoute>
              }
            />
          )}
          {featureFlags.hex_embed && (
            <Route
              path={'/hex-analytics'}
              element={
                <RestrictedRoute requiredPermissions={[[Permission.VIEW_THOUGHTSPOT]]}>
                  <HexAnalyticsPage />
                </RestrictedRoute>
              }
            />
          )}
          {featureFlags.price_benchmarking && (
            <Route
              path={'/cost-intelligence'}
              element={
                <RestrictedRoute requiredPermissions={[[Permission.VIEW_PRICE_BENCHMARK]]}>
                  <CostIntelligencePage />
                </RestrictedRoute>
              }
            />
          )}
        </>
        {featureFlags.advise && (
          <>
            <Route
              path={'/advise'}
              element={
                <RestrictedRoute requiredPermissions={[[Permission.ADVISE_VIEW, Permission.ADVISE_FULL]]}>
                  <AdviseDashboardPage />
                </RestrictedRoute>
              }
            />
            <Route
              path={'/advise/suppliers'}
              element={
                <RestrictedRoute requiredPermissions={[[Permission.ADVISE_VIEW, Permission.ADVISE_FULL]]}>
                  <AdviseSuppliersPage />
                </RestrictedRoute>
              }
            />
            <Route
              path={'/advise/customers'}
              element={
                <RestrictedRoute requiredPermissions={[[Permission.ADVISE_VIEW, Permission.ADVISE_FULL]]}>
                  <AdviseCustomersPage />
                </RestrictedRoute>
              }
            />
            <Route
              path={'/advise/engagements'}
              element={
                <RestrictedRoute requiredPermissions={[[Permission.ADVISE_VIEW, Permission.ADVISE_FULL]]}>
                  <AdviseEngagementsPage />
                </RestrictedRoute>
              }
            />
            <Route
              path={'/advise/analysis'}
              element={
                <RestrictedRoute requiredPermissions={[[Permission.ADVISE_VIEW, Permission.ADVISE_FULL]]}>
                  <AdviseAnalysisPage />
                </RestrictedRoute>
              }
            />
            <Route
              path={'/advise/reports'}
              element={
                <RestrictedRoute requiredPermissions={[[Permission.ADVISE_VIEW, Permission.ADVISE_FULL]]}>
                  <AdviseReportsPage />
                </RestrictedRoute>
              }
            />
            <Route
              path={'/advise/report-editor'}
              element={
                <RestrictedRoute requiredPermissions={[[Permission.ADVISE_VIEW, Permission.ADVISE_FULL]]}>
                  <AdviseReportEditorPage />
                </RestrictedRoute>
              }
            />
            <Route
              path={'/advise/tasks'}
              element={
                <RestrictedRoute requiredPermissions={[[Permission.ADVISE_VIEW, Permission.ADVISE_FULL]]}>
                  <AdviseTasksPage />
                </RestrictedRoute>
              }
            />
            <Route
              path={'/advise/tools'}
              element={
                <RestrictedRoute requiredPermissions={[[Permission.ADVISE_VIEW, Permission.ADVISE_FULL]]}>
                  <AdviseAgentToolsPage />
                </RestrictedRoute>
              }
            />
            <Route
              path={'/advise/admin/users'}
              element={
                <RestrictedRoute requiredPermissions={[[Permission.ADVISE_FULL], [Permission.VIEW_ADMIN_PAGE]]}>
                  <AdviseUserManagementPage />
                </RestrictedRoute>
              }
            />
          </>
        )}
        {process.env.NODE_ENV === 'development' && (
          <Route
            path="/style-guide"
            element={<StyleGuide />}
          />
        )}
      </Route>
    </Routes>
  );
}
